import { useState, useEffect, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';

const RECENT_ORDERS_PAGE_SIZE = 10;

// Every billing figure comes straight from GET /metrics/billing — no sums,
// averages or date-bucketing happen client-side (CHANGES.md decision 4).
// Recent orders / buy-again / coupons are arrays consumed and displayed
// as-is, the same way Admin Dashboard's recentOrders/lowStock lists are.
const useCustomerDashboardPage = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const [searchParams] = useSearchParams();
  const previewState = searchParams.get('previewState');

  const [userId] = useState(() => localStorage.getItem('userId') || '');

  const [status, setStatus] = useState('loading');
  const [errorInfo, setErrorInfo] = useState(null);
  const [billing, setBilling] = useState(null);
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [reminderInvoices, setReminderInvoices] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAll = useCallback((isRefresh = false) => {
    if (!config?.backendUrl || !userId) return;
    if (isRefresh) setRefreshing(true); else setStatus('loading');

    // Payment reminders are a courtesy card, never a reason to block the
    // dashboard — fetched alongside but failing quietly.
    axios.get(`${config.backendUrl}/invoices/my`, { params: { status: 'issued', page: 1, pageSize: 25 } })
      .then((res) => setReminderInvoices((res.data.items || []).filter((inv) => inv.reminderSentAt)))
      .catch((error) => console.error('Error fetching invoice reminders', error));

    Promise.all([
      axios.get(`${config.backendUrl}/metrics/billing`, { params: { userId } }),
      axios.get(`${config.backendUrl}/orders`, { params: { userId, page: 1, pageSize: RECENT_ORDERS_PAGE_SIZE, sort: 'newest' } }),
      axios.get(`${config.backendUrl}/coupons/my`),
    ])
      .then(([billingRes, ordersRes, couponsRes]) => {
        setBilling(billingRes.data);
        setOrders(ordersRes.data.items);
        setCoupons(couponsRes.data);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching dashboard data', error);
        if (error.response?.status === 403 || error.response?.status === 401) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /metrics/billing · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      })
      .finally(() => setRefreshing(false));
  }, [config, userId, t]);

  useEffect(() => { fetchAll(false); }, [fetchAll]);

  const handleRefresh = () => fetchAll(true);

  const isPreviewEmptyKind = previewState === 'empty';
  const effectiveStatus = previewState || status;
  const effectiveBilling = isPreviewEmptyKind ? {
    outstandingTotal: 0, unpaidCount: 0, paidThisYear: 0, paidCount: 0,
    monthlySpend: [], ordersThisMonth: 0, spendThisMonth: 0,
  } : billing;
  const effectiveOrders = isPreviewEmptyKind ? [] : orders;
  const effectiveCoupons = isPreviewEmptyKind ? [] : coupons;

  // "Buy again": the most recent distinct products from order history —
  // reads the already-fetched orders array, same as Admin Dashboard reading
  // its recentOrders/lowStock arrays; no aggregation across them.
  const reorderItems = [];
  const seenProducts = new Set();
  for (const order of effectiveOrders) {
    const items = order.orderItems?.length
      ? order.orderItems
      : (order.productId ? [{ productId: order.productId, productName: order.productName, unitPrice: order.totalPrice / (order.quantity || 1) }] : []);
    for (const item of items) {
      if (item.productId && !seenProducts.has(item.productId)) {
        seenProducts.add(item.productId);
        reorderItems.push({ ...item, lastOrderedAt: order.createdAt });
      }
      if (reorderItems.length >= 4) break;
    }
    if (reorderItems.length >= 4) break;
  }

  return {
    t, effectiveStatus, billing: effectiveBilling, orders: effectiveOrders, coupons: effectiveCoupons,
    reminderInvoices: isPreviewEmptyKind ? [] : reminderInvoices,
    reorderItems, errorInfo, refreshing, handleRefresh, fetchAll,
  };
};

export default useCustomerDashboardPage;
